import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from '@uidotdev/usehooks'
import { AxiosError } from 'axios'
import { ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { z } from 'zod'

import { Toast } from '@/common/components/Toast'
import { OrderBy, useOrderBy } from '@/common/hooks/useOrderBy'
import { accountModel } from '@/common/models/account'
import { applicationAccountModel } from '@/common/models/applicationAccount'
import { calculateItemsPerPage } from '@/common/utils/calculateItemsPerPage'
import { api, ApiError, APIPaginated, ApiWhere } from '@/lib/axios'
import { FilterAccountsForm } from '../schemas/FilterAccountsSchema'
import { useFilterAccounts } from './useFilterAccounts'

export const accountPaginatedDTO = accountModel.extend({
  ApplicationAccount: z.array(
    applicationAccountModel.extend({
      Application: z.object({
        title: z.string(),
      }),
    }),
  ),
})

export type AccountPaginated = z.infer<typeof accountPaginatedDTO>

export type GetPaginateAccountsProps = {
  page: number
  orderBy: OrderBy
  filters: FilterAccountsForm
}

async function getPaginateAccounts({
  page,
  orderBy,
  filters,
}: GetPaginateAccountsProps) {
  const where: ApiWhere = {
    isActive: !filters.isActive,
    ...(filters.country && { country: filters.country }),
    ...(filters.tag && {
      ApplicationAccount: {
        some: { tag: { array_contains: filters.tag.toUpperCase() } },
      },
    }),
    ...(filters.locale && { locale: { contains: filters.locale } }),
    ...(filters.freeSearch && {
      AND: [
        {
          OR: [
            { title: { contains: filters.freeSearch } },
            { accountKey: { contains: filters.freeSearch } },
            { description: { contains: filters.freeSearch } },
          ],
        },
      ],
    }),
  }

  const { data } = await api.backend.get<APIPaginated<AccountPaginated>>(
    '/account/paginate',
    {
      params: {
        page,
        limit: 25,
        relations: {
          orderBy,
          where,
          include: {
            ApplicationAccount: {
              where: {
                isActive: true,
              },
              include: {
                Application: {
                  select: {
                    title: true,
                  },
                },
              },
            },
          },
        },
      },
    },
  )

  return data
}

export const usePaginateAccounts = () => {
  const [tableSearch, setTableSearch] = useState('')
  const [tableSearchResult, setTableSearchResult] = useState<
    AccountPaginated[]
  >([])
  const [page, setPage] = useState(1)

  const [filters] = useFilterAccounts()
  const { t } = useTranslation(['common', 'sso'])
  const debouncedTableSearch = useDebounce(tableSearch, 300)
  const { orderBy, handleChangeOrder } = useOrderBy({ title: 'desc' })
  const { data, isLoading, error } = useQuery<
    APIPaginated<AccountPaginated>,
    AxiosError<ApiError>
  >({
    queryKey: ['/account/paginate', page, orderBy, ...Object.values(filters)],
    queryFn: () => getPaginateAccounts({ page, orderBy, filters }),
  })

  const handleAccountsSearch = (search: string) => {
    setTableSearch(search?.toLowerCase() || '')
  }

  const handleChangePage = (page: number) => {
    setPage(page)
  }

  const accountsBadges = useMemo(() => {
    return {
      locale: new Set(
        data?.items?.map((account) => account.locale) || [],
      ).size.toFixed(0),
      country: new Set(
        data?.items?.map((account) => account.country) || [],
      ).size.toFixed(0),
    }
  }, [data])

  useEffect(() => {
    if (!error) return

    toast(Toast, {
      data: {
        type: 'danger',
        icon: ServerCrash,
        title: t('sso:accounts.paginate_table.toast_error.title'),
        description:
          error?.response?.data?.friendlyMessage ||
          t('sso:accounts.paginate_table.toast_error.description'),
      },
    })
  }, [error, t])

  useEffect(() => {
    if (!data || data?.items?.length === 0) return

    const filteredData = data.items.filter(
      (account) =>
        account.title.includes(debouncedTableSearch) ||
        account.description?.includes(debouncedTableSearch) ||
        account.accountKey.includes(debouncedTableSearch),
    )

    setTableSearchResult(filteredData)
  }, [data, debouncedTableSearch, t])

  return {
    accounts: debouncedTableSearch ? tableSearchResult : data?.items || [],
    accountsPaginated: data?.meta,
    isLoadingAccounts: isLoading,
    accountsOrderBy: orderBy,
    accountsBadges,
    accountsPerPage: calculateItemsPerPage(data?.meta),
    filtersQty: Object.values(filters).filter(Boolean).length,
    handleAccountsSearch: handleAccountsSearch,
    handleChangePage,
    handleChangeOrder,
  }
}
